import React from "react";

import "./SummaryCard.css";

function SummaryCardSection({
  title,
  badge,
  badgeTone = "cool",
  headerExtra,
  value,
  valueTone = "default",
  valueLabel,
  detail,
  detailMuted = false,
  detailInline = false,
  meta,
  footer,
  footerTone = "default",
  footerSeparated = false,
  decoration,
  watermark,
  disabled = false,
}) {
  const valueClass = [
    "SummaryCard__Value",
    valueTone === "positive" ? "SummaryCard__Value--positive" : "",
    valueTone === "secondary" ? "SummaryCard__Value--secondary" : "",
    valueTone === "muted" || disabled ? "SummaryCard__Value--muted" : "",
  ].filter(Boolean).join(" ");

  const detailClass = [
    "SummaryCard__Detail",
    detailMuted || disabled ? "SummaryCard__Detail--muted" : "",
    !detailInline ? "SummaryCard__Detail--block" : "",
  ].filter(Boolean).join(" ");

  const footerClass = [
    "SummaryCard__Footer",
    footerTone === "positive" ? "SummaryCard__Footer--positive" : "",
    footerTone === "muted" || (disabled && footerTone !== "alert") ? "SummaryCard__Footer--muted" : "",
    footerTone === "alert" ? "SummaryCard__Footer--alert" : "",
    footerSeparated ? "SummaryCard__Footer--separated" : "",
  ].filter(Boolean).join(" ");

  const sectionClass = [
    "SummaryCard__Section",
    decoration ? "SummaryCard__Section--withDecoration" : "",
    disabled ? "SummaryCard__Section--disabled" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={sectionClass}>
      {watermark && (
        <img
          src={watermark}
          alt=""
          aria-hidden
          className="SummaryCard__Watermark"
        />
      )}

      <div className="SummaryCard__Content">
        {(title || badge || headerExtra) && (
          <header className="SummaryCard__Header">
            <div className="SummaryCard__TitleRow">
              {title && <h3 className="SummaryCard__Title">{title}</h3>}
              {badge && (
                <span className={`SummaryCard__Badge SummaryCard__Badge--${badgeTone}`}>
                  {badge}
                </span>
              )}
            </div>
            {headerExtra && (
              <div className="SummaryCard__HeaderExtra">{headerExtra}</div>
            )}
          </header>
        )}

        {(valueLabel || value != null || detail) && (
          <div className="SummaryCard__Body">
            {valueLabel && <p className="SummaryCard__ValueLabel">{valueLabel}</p>}
            {value != null && <p className={valueClass}>{value}</p>}
            {detail && <p className={detailClass}>{detail}</p>}
          </div>
        )}

        {meta && <div className="SummaryCard__Meta">{meta}</div>}
      </div>

      {decoration && (
        <div className="SummaryCard__Decoration" aria-hidden>
          {decoration}
        </div>
      )}

      {footer && <div className={footerClass}>{footer}</div>}
    </div>
  );
}

export default function SummaryCard({
  title,
  badge,
  badgeTone,
  headerExtra,
  value,
  valueTone,
  valueLabel,
  detail,
  detailMuted,
  detailInline,
  meta,
  footer,
  footerTone,
  footerSeparated,
  decoration,
  watermark,
  disabled = false,
  sections,
  className = "",
}) {
  const isSplit = Array.isArray(sections) && sections.length > 1;
  const items = isSplit
    ? sections.map((section) => ({
      ...section,
      disabled: section.disabled ?? disabled,
    }))
    : [{
      title,
      badge,
      badgeTone,
      headerExtra,
      value,
      valueTone,
      valueLabel,
      detail,
      detailMuted,
      detailInline,
      meta,
      footer,
      footerTone,
      footerSeparated,
      decoration,
      watermark,
      disabled,
    }];

  const allSectionsDisabled = items.length > 0 && items.every((section) => section.disabled);
  const cardDisabled = disabled || allSectionsDisabled;

  const rootClass = [
    "SummaryCard",
    isSplit ? "SummaryCard--split" : "",
    cardDisabled ? "SummaryCard--disabled" : "",
    className,
  ].filter(Boolean).join(" ");

  return (
    // eslint-disable-next-line jsx-a11y/role-supports-aria-props
    <article className={rootClass} aria-disabled={cardDisabled || undefined}>
      {items.map((section, index) => (
        <SummaryCardSection
          key={section.key || section.title || index}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...section}
        />
      ))}
    </article>
  );
}
